import React, { useState, useMemo } from 'react';
import { Button } from 'prosci-ui';
import { useHubAppCopy } from '../../hooks';
import {
  OnboardingFormData,
  YEARS_EXPERIENCE_OPTIONS,
  PERCENT_TIME_OPTIONS,
  COMPETENCY_OPTIONS,
  LOCALE_OPTIONS
} from './types';
import { SHORT_STRING_INPUT_MAX } from '../../config';

interface OnboardingFormProps {
  initialValues: Partial<OnboardingFormData>;
  onSubmit: (data: OnboardingFormData) => Promise<void>;
  loading?: boolean;
}

export const OnboardingForm: React.FC<OnboardingFormProps> = ({
  initialValues,
  onSubmit,
  loading = false
}) => {
  const hubAppCopy = useHubAppCopy();
  const copy = hubAppCopy('portalCopy')?.onboarding;

  const [firstName, setFirstName] = useState(initialValues.firstName || '');
  const [lastName, setLastName] = useState(initialValues.lastName || '');
  const [title, setTitle] = useState(initialValues.title || '');
  const [preferredLocale, setPreferredLocale] = useState(initialValues.preferredLocale || '');
  const [yearsExperience, setYearsExperience] = useState(initialValues.yearsExperience || '');
  const [percentTime, setPercentTime] = useState(initialValues.percentTimeOnChangeManagement || '');
  const [competency, setCompetency] = useState(initialValues.currentChangeManagementCompetency || '');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Build options arrays from copy with fallbacks
  const localeOptions = useMemo(() => {
    const copyOptions = copy?.preferredLocale?.options || {};
    return LOCALE_OPTIONS.map(({ value, label, copyKey }) => ({
      value,
      label: copyOptions[copyKey] || label
    }));
  }, [copy]);

  const yearsExperienceOptions = useMemo(() => {
    const copyOptions = copy?.yearsExperience?.options || {};
    return YEARS_EXPERIENCE_OPTIONS.map(({ value, label, copyKey }) => ({
      value,
      label: copyOptions[copyKey] || label
    }));
  }, [copy]);

  const percentTimeOptions = useMemo(() => {
    const copyOptions = copy?.percentTimeOnChangeManagement?.options || {};
    return PERCENT_TIME_OPTIONS.map(({ value, label, copyKey }) => ({
      value,
      label: copyOptions[copyKey] || label
    }));
  }, [copy]);

  const competencyOptions = useMemo(() => {
    const copyOptions = copy?.currentChangeManagementCompetency?.options || {};
    return COMPETENCY_OPTIONS.map(({ value, label, copyKey }) => ({
      value,
      label: copyOptions[copyKey] || label
    }));
  }, [copy]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = copy?.validation?.firstNameRequired || 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = copy?.validation?.lastNameRequired || 'Last name is required';
    }

    if (!title.trim()) {
      newErrors.title = copy?.validation?.jobTitleRequired || 'Job title is required';
    } else if (title.trim().length > SHORT_STRING_INPUT_MAX) {
      newErrors.title = `Job title must be ${SHORT_STRING_INPUT_MAX} characters or less`;
    }

    if (!preferredLocale) {
      newErrors.preferredLocale = copy?.validation?.preferredLocaleRequired || 'Please select a preferred locale';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData: OnboardingFormData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      title: title.trim(),
      preferredLocale,
      profileLastUpdated: new Date().toISOString(),
      ...(yearsExperience && { yearsExperience }),
      ...(percentTime && { percentTimeOnChangeManagement: percentTime }),
      ...(competency && { currentChangeManagementCompetency: competency })
    };

    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="cert-modal__form">
      <div className="cert-modal__form-row">
        <div className="cert-modal__form-group cert-modal__form-group--half">
          <label htmlFor="firstName" className="cert-modal__label">
            {copy?.firstName?.label || 'First Name'}<span className="cert-modal__required">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            className={`cert-modal__input ${errors.firstName ? 'cert-modal__input--error' : ''}`}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={loading}
          />
          {errors.firstName && <span className="cert-modal__error">{errors.firstName}</span>}
        </div>

        <div className="cert-modal__form-group cert-modal__form-group--half">
          <label htmlFor="lastName" className="cert-modal__label">
            {copy?.lastName?.label || 'Last Name'}<span className="cert-modal__required">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            className={`cert-modal__input ${errors.lastName ? 'cert-modal__input--error' : ''}`}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={loading}
          />
          {errors.lastName && <span className="cert-modal__error">{errors.lastName}</span>}
        </div>
      </div>

      <div className="cert-modal__form-group">
        <label htmlFor="title" className="cert-modal__label">
          {copy?.jobTitle?.label || 'Job Title'}<span className="cert-modal__required">*</span>
        </label>
        <input
          id="title"
          type="text"
          className={`cert-modal__input ${errors.title || title.length > SHORT_STRING_INPUT_MAX ? 'cert-modal__input--error' : ''}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={SHORT_STRING_INPUT_MAX}
          disabled={loading}
        />
        <span className={`cert-modal__character-count ${title.length > SHORT_STRING_INPUT_MAX ? 'cert-modal__character-count--error' : ''}`}>
          {title.length}/{SHORT_STRING_INPUT_MAX} characters
        </span>
        {errors.title && <span className="cert-modal__error">{errors.title}</span>}
      </div>

      <div className="cert-modal__form-group">
        <label htmlFor="preferredLocale" className="cert-modal__label">
          {copy?.preferredLocale?.label || 'Preferred Locale'}<span className="cert-modal__required">*</span>
        </label>
        <select
          id="preferredLocale"
          className={`cert-modal__input ${errors.preferredLocale ? 'cert-modal__input--error' : ''}`}
          value={preferredLocale}
          onChange={(e) => setPreferredLocale(e.target.value)}
          disabled={loading}
        >
          <option value="">{copy?.preferredLocale?.placeholder || 'Select a locale'}</option>
          {localeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.preferredLocale && <span className="cert-modal__error">{errors.preferredLocale}</span>}
      </div>

      <div className="cert-modal__form-group">
        <label className="cert-modal__label">
          {copy?.yearsExperience?.label || 'How many years of experience do you have in your current role?'}
        </label>
        <div className="cert-modal__radio-group">
          {yearsExperienceOptions.map(option => (
            <div
              key={option.value}
              className={`cert-modal__radio-option ${yearsExperience === option.value ? 'cert-modal__radio-option--selected' : ''}`}
              onClick={() => !loading && setYearsExperience(option.value)}
            >
              <input
                type="radio"
                id={`years-${option.value}`}
                name="yearsExperience"
                value={option.value}
                checked={yearsExperience === option.value}
                onChange={(e) => setYearsExperience(e.target.value)}
                disabled={loading}
              />
              <label htmlFor={`years-${option.value}`}>
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="cert-modal__form-group">
        <label className="cert-modal__label">
          {copy?.percentTimeOnChangeManagement?.label || 'What percentage of your time is/will be focused on change management?'}
        </label>
        <div className="cert-modal__radio-group">
          {percentTimeOptions.map(option => (
            <div
              key={option.value}
              className={`cert-modal__radio-option ${percentTime === option.value ? 'cert-modal__radio-option--selected' : ''}`}
              onClick={() => !loading && setPercentTime(option.value)}
            >
              <input
                type="radio"
                id={`time-${option.value}`}
                name="percentTime"
                value={option.value}
                checked={percentTime === option.value}
                onChange={(e) => setPercentTime(e.target.value)}
                disabled={loading}
              />
              <label htmlFor={`time-${option.value}`}>
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="cert-modal__form-group">
        <label className="cert-modal__label">
          {copy?.currentChangeManagementCompetency?.label || 'Which one of the following levels most accurately describes your current change management competency?'}
        </label>
        <div className="cert-modal__radio-group">
          {competencyOptions.map(option => (
            <div
              key={option.value}
              className={`cert-modal__radio-option ${competency === option.value ? 'cert-modal__radio-option--selected' : ''}`}
              onClick={() => !loading && setCompetency(option.value)}
            >
              <input
                type="radio"
                id={`competency-${option.value.replace(/\s+/g, '-').toLowerCase()}`}
                name="competency"
                value={option.value}
                checked={competency === option.value}
                onChange={(e) => setCompetency(e.target.value)}
                disabled={loading}
              />
              <label htmlFor={`competency-${option.value.replace(/\s+/g, '-').toLowerCase()}`}>
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="cert-modal__actions">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="cert-modal__button cert-modal__button--submit"
        >
          {loading ? copy?.submitButton?.submitting || 'Saving...' : copy?.submitButton?.submit || 'Continue'}
        </Button>
      </div>
    </form>
  );
};

export default OnboardingForm;
